import cn from "classnames";
import camelCase from "lodash/camelCase";
import React, {
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react";

import { WSAvatar } from "../common/WSAvatar/WSAvatar.component";
import { WSSidebar } from "../common/WSSidebar/WSSidebar.component";
import { WSIcon } from "../core/WSIcon/WSIcon.component";
import { WSText } from "../core/WSText/WSText.component";
import { useIsMobile, WSScreen } from "../layout";
import { WSActions } from "../WSActions/WSActions";
import { WSDivider } from "../WSDivider/WSDivider";
import { WSElement, WSPaddingProps } from "../WSElement/WSElement.component";

import { modalIconSizeMap, modalPaddingMap, WSModalProps } from "./utils";
import styles from "./WSModal.module.scss";


const CONFIRM_CLOSE_MESSAGE =
  "Are you sure you want to close? Your work won't be saved.";

export const WSModal: React.FC<WSModalProps> = (props) => {
  const isMobile = useIsMobile();

  const {
    blockClose,
    onClose,
    onExit,
    title,
    titleAlign = "left",
    children,
    className,
    pullOver,
    showCloseIcon = true,
    fullScreen,
    centered = false,
    fullScreenOnMobile,
    size = "M",
    allowClickOutsideToClose,
    allowClickOutsideWithConfirmationToClose,
    divider = true,
    avatar,
    actions,
    headerActions,
    footer,
    colorBackground = "white",
    ...elementProps
  } = props;

  // Keep latest callback refs to avoid cleanup firing on reference changes
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  // Lock body scroll
  useEffect(() => {
    document.body.classList.add(styles.withModal);
    return () => {
      document.body.classList.remove(styles.withModal);
    };
  }, []);

  useEffect(
    () => () => {
      onExitRef.current?.("system");
      onCloseRef.current?.(null);
    },
    []
  );

  // Single close handler
  const handleClose = useCallback(() => {
    if (!blockClose) {
      onExitRef.current?.("user");
      onCloseRef.current?.(null);
    }
  }, [blockClose]);

  const titleId = useMemo(
    () =>
      typeof title === "string" && title ? `${camelCase(title)}-title` : undefined,
    [title]
  );

  const isFullScreen = useMemo(() => {
    if (fullScreen) {
      return true;
    } else if (fullScreenOnMobile) {
      return isMobile;
    } else {
      return false;
    }
  }, [fullScreen, fullScreenOnMobile, isMobile]);

  const headerPaddings = useMemo(() => {
    let paddings: WSPaddingProps;

    if (isFullScreen) {
      paddings = {
        pt: "M",
        pl: "L",
        pr: "L",
        pb: "M"
      };
    } else {
      paddings = {
        pt: modalPaddingMap[size],
        pl: modalPaddingMap[size],
        pr: modalPaddingMap[size],
        pb: "S"
      };
    }

    return paddings;
  }, [isFullScreen, size]);

  const renderBody = useCallback(
    () => (
      <>
        <WSElement className={styles.header} {...headerPaddings}>
          {avatar && <WSAvatar {...avatar} />}
          <WSText
            id={titleId}
            kind={isFullScreen ? "ParagraphSm" : "Paragraph"}
            align={titleAlign}
            className={styles.title}
            weight="medium"
          >
            {title}
          </WSText>
          {headerActions && (
            <WSActions
              className={styles.headerActions}
              orientation="horizontal"
              alignment="right"
              {...headerActions}
            />
          )}
          {showCloseIcon && (
            <WSIcon
              block
              name="exit"
              role="button"
              aria-label="Close"
              size={modalIconSizeMap[size]}
              onClick={handleClose}
              className={cn(styles.closeIcon, blockClose && styles.blockClose)}
            />
          )}
        </WSElement>
        {divider && <WSDivider />}
        <WSElement
          p={modalPaddingMap[size]}
          className={styles.content}
          colorBackground={colorBackground}
        >
          {children}
        </WSElement>
        {actions && (
          <>
            {divider && <WSDivider />}
            <WSElement
              className={styles.actions}
              p={modalPaddingMap[size]}
              pb={modalPaddingMap[size]}
            >
              <WSActions
                orientation="horizontal"
                alignment="right"
                {...actions}
              />
            </WSElement>
          </>
        )}
        {footer}
      </>
    ),
    [
      title,
      titleId,
      children,
      handleClose,
      showCloseIcon,
      blockClose,
      size,
      avatar,
      actions,
      footer
    ]
  );

  // Handle Escape button press
  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === "Escape") {
      handleClose();
    }
  };

  return (
    <>
      <WSScreen.Mobile>
        {pullOver || isFullScreen ? (
          <>
            <WSElement className={styles.overlay} onClick={handleClose} />
            <WSElement
              className={cn(
                styles.base,
                {
                  [styles.fullScreen]: isFullScreen,
                  [styles.centered]: centered
                },
                className
              )}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              ref={(element) => {
                if (element) {
                  element.focus();
                  element.scrollTop = 0;
                }
              }}
              colorBackground={colorBackground}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              {...elementProps}
            >
              {renderBody()}
            </WSElement>
          </>
        ) : (
          <WSSidebar
            onClose={handleClose}
            noCloseIcon
            className={cn(styles.drawerBase, className)}
            onKeyDown={handleKeyDown}
            position="bottom"
            contentElementProps={{
              colorBackground,
              p: "NONE"
            }}
            size="AUTO"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            {...elementProps}
          >
            {renderBody()}
          </WSSidebar>
        )}
      </WSScreen.Mobile>
      <WSScreen.TabletAndDesktop>
        <WSElement
          data-active-modal
          className={cn(styles.wrapper, {
            [styles.blockClose]: blockClose,
            [styles.wrapperFullScreen]: isFullScreen
          })}
          onClick={
            allowClickOutsideToClose || allowClickOutsideWithConfirmationToClose
              ? (event) => {
                  if (event.target === event.currentTarget) {
                    if (allowClickOutsideToClose) {
                      handleClose();
                    } else if (allowClickOutsideWithConfirmationToClose) {
                      if (window.confirm(CONFIRM_CLOSE_MESSAGE)) {
                        handleClose();
                      }
                    }
                  }
                }
              : undefined
          }
        >
          <WSElement
            className={cn(
              styles.base,
              {
                [styles.fullScreen]: isFullScreen,
                [styles.centered]: centered
              },
              className,
              styles[`size-${size}`]
            )}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            ref={(element) => {
              if (element) {
                element.focus();
                element.scrollTop = 0;
              }
            }}
            colorBackground={colorBackground}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            {...elementProps}
          >
            {renderBody()}
          </WSElement>
        </WSElement>
      </WSScreen.TabletAndDesktop>
    </>
  );
};

WSModal.displayName = "WSModal";
